import { spannungsebene } from '../../../../utils/apiTypes';
import { spannungsebeneState } from './spannungsebenenTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, putWithAuth, deleteWithAuth } from "../../../../utils/api";
import type { RootState, AppDispatch } from "../../../../app/store";
import { logError } from "../../../../utils/utils";
import { openSnackbar } from "../../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../../utils/errorHandling';

export const spannungsebeneDummy: spannungsebene
  = {
  ebene: "",
  kommentar: "",
  zugeordnet: false,
  spannungswert: 0,
};

const initialState: spannungsebeneState
  = {
  spannungsebeneListe: [],
  alteSpannungsebene: spannungsebeneDummy,
  aktuelleSpannungsebene: spannungsebeneDummy,
  newEntry: false,
  editEntry: false,
  editDirty: 0,
  viewMode: false,
  error: [],
  listLoading: false,
  sorting: [
    {
      field: 'ebene',
      sort: 'asc',
    },
  ]
};

const spannungsebenenSlice = createSlice({
  name: "spannungsebene",
  initialState,
  reducers:
  {
    updateSpannungsebenen: (state, action: PayloadAction<spannungsebene>) => {
      const updateIndex = state.spannungsebeneListe.findIndex(
        (x: spannungsebene) => x.ebene == action.payload.ebene
      );
      const firstPart = state.spannungsebeneListe.slice(0, updateIndex);
      let lastPart = Array<spannungsebene>();
      if (updateIndex + 1 < state.spannungsebeneListe.length)
        lastPart = state.spannungsebeneListe.slice(updateIndex + 1);
      state.spannungsebeneListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.ebene.localeCompare(b.ebene));
    },
    removeFromSpannungsebenen: (state, action: PayloadAction<spannungsebene>) => {
      const updateIndex = state.spannungsebeneListe.findIndex(
        (x) => x.ebene == action.payload.ebene
      );
      const firstPart = state.spannungsebeneListe.slice(0, updateIndex);
      let lastPart = Array<spannungsebene>();
      if (updateIndex + 1 < state.spannungsebeneListe.length)
        lastPart = state.spannungsebeneListe.slice(updateIndex + 1);
      state.spannungsebeneListe = [...firstPart, ...lastPart];
    },
    addAktuellesSpannungsebeneToSpannungsebenen: (state) => {
      const spannungsebeneArray = state.spannungsebeneListe;
      spannungsebeneArray.push(state.aktuelleSpannungsebene);
      state.spannungsebeneListe = [...spannungsebeneArray].sort((a, b) => a.ebene.localeCompare(b.ebene));
    },
    setSpannungsebenen: (state, action: PayloadAction<Array<spannungsebene>>) => {
      state.spannungsebeneListe = action.payload;
    },
    setAktuellesSpannungsebene: (state, action: PayloadAction<spannungsebene>) => {
      state.aktuelleSpannungsebene = action.payload;
      state.alteSpannungsebene = action.payload;
    },
    setAltesSpannungsebene: (state, action: PayloadAction<spannungsebene>) => {
      state.alteSpannungsebene = action.payload;
    },
    resetAktuellesSpannungsebene: (state) => {
      state.aktuelleSpannungsebene = state.alteSpannungsebene;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setEbene: (state, action: PayloadAction<string>) => {
      state.aktuelleSpannungsebene.ebene = action.payload;
      state.editDirty =
        state.aktuelleSpannungsebene.ebene ==
          state.alteSpannungsebene.ebene
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setSpannungswert: (state, action: PayloadAction<number>) => {
      state.aktuelleSpannungsebene.spannungswert = action.payload;
      state.editDirty =
        state.aktuelleSpannungsebene.spannungswert ==
          state.alteSpannungsebene.spannungswert
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuelleSpannungsebene.kommentar = action.payload;
      state.editDirty =
        state.aktuelleSpannungsebene.kommentar ==
          state.alteSpannungsebene.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  }
});


//Selectors
//======================================================================
export const selectSpannungsebeneListe = (state: RootState): Array<spannungsebene> =>
  state.spannungsebene.spannungsebeneListe;
export const selectAktuellesSpannungsebene = (state: RootState): spannungsebene =>
  state.spannungsebene.aktuelleSpannungsebene;
export const selectEditEntry = (state: RootState): boolean =>
  state.spannungsebene.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.spannungsebene.newEntry;
export const selectEditDirty = (state: RootState): number =>
  state.spannungsebene.editDirty;
export const selectViewMode = (state: RootState): boolean => state.spannungsebene.viewMode;
export const selectError = (state: RootState): boolean => state.spannungsebene.error.length > 0;
export const selectListLoading = (state: RootState): boolean => state.spannungsebene.listLoading;
export const selectSorting = (state: RootState): GridSortModel => state.spannungsebene.sorting;

//Actions
//=====================================================================

export const getSpannungsebenen = () => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth("SPANNUNGSEBENE", {})
    .then((response) => {
      dispatch(setSpannungsebenen(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateSpannungsebene = (aktuelleSpannungsebene: spannungsebene): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("SPANNUNGSEBENEID", {}, aktuelleSpannungsebene, aktuelleSpannungsebene.ebene)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Spannungsebene ${aktuelleSpannungsebene.ebene} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAltesSpannungsebene(aktuelleSpannungsebene));
      dispatch(updateSpannungsebenen(aktuelleSpannungsebene));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const updateAktuellesSpannungsebene = (aktuelleSpannungsebene: spannungsebene): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setAktuellesSpannungsebene(aktuelleSpannungsebene));
  dispatch(setViewMode());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSpannungsebene());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSpannungsebene());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellesSpannungsebene(spannungsebeneDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createSpannungsebene = (aktuelleSpannungsebene: spannungsebene): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("SPANNUNGSEBENEID", {}, aktuelleSpannungsebene, aktuelleSpannungsebene.ebene)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Spannungsebene ${aktuelleSpannungsebene.ebene} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );

      dispatch(addAktuellesSpannungsebeneToSpannungsebenen());
      dispatch(setAktuellesSpannungsebene(spannungsebeneDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const deleteSpannungsebene = (aktuelleSpannungsebene: spannungsebene): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth("SPANNUNGSEBENEID", { name: aktuelleSpannungsebene.ebene }, aktuelleSpannungsebene.ebene)
    .then(() => {
      dispatch(setAktuellesSpannungsebene(spannungsebeneDummy));
      dispatch(removeFromSpannungsebenen(aktuelleSpannungsebene));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(openSnackbar({
        message: `Spannungsebene ${aktuelleSpannungsebene.ebene} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const {
  removeFromSpannungsebenen,
  setSpannungsebenen,
  setAktuellesSpannungsebene,
  resetAktuellesSpannungsebene,
  setAltesSpannungsebene,
  updateSpannungsebenen,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setEbene,
  setSpannungswert,
  setKommentar,
  addAktuellesSpannungsebeneToSpannungsebenen,
  setListLoading,
  setSorting
} = spannungsebenenSlice.actions;
export default spannungsebenenSlice.reducer;